{/*
  * DspViewabilityPage.tsx
  * https://dash.applovin.com/documentation/dsp/viewability
  * Documentation MAX Demand Partners > DSPs > Viewability
  */}
import React, { FunctionComponent } from 'react';
import {
    AlertMessage,
    AlertMessageType,
    DocsPageModule,
    LabelFontFamily
} from 'UI-Dash-Commons-UI';
import CodeSnippet from '../containers/CodeSnippetContainer';

type DspViewabilityPageProps = {
    linkTargetTopPosition?: number;
}

const DspViewabilityPage: FunctionComponent<DspViewabilityPageProps> = ( { linkTargetTopPosition } ) =>
{
    return (
        <>
            <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'Viewability'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_1}/>

            <DocsPageModule.DocsPageContent
                text={`AppLovin integrates the Open Measurement (OM) SDK and thereby allows you to measure Viewability for ALX creatives. `
                    + `AppLovin supports OM measurement for VAST on SDK versions 11.0+, and for additional formats on SDK versions 11.3.0+.`}
            />
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={`The formats supported on SDK 11.3.0+ are:`} />
                <DocsPageModule.DocsPageUnorderedList>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={`Banner HTML/MRAID`} />
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={`MREC HTML/MRAID`} />
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={`Interstitial VAST XML and VAST Tags`} />
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={`Interstitial HTML/MRAID`} />
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={`Native`} />
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={`Rewarded VAST XML and VAST tags`} />
                    </DocsPageModule.DocsPageBulletPoint>
                </DocsPageModule.DocsPageUnorderedList>
            </DocsPageModule.DocsPageContent>

            <AlertMessage type={AlertMessageType.INFO}>
                <DocsPageModule.DocsPageContentText text={`Note:`} fontFamily={LabelFontFamily.FONT_FAMILY_DEMI} />
                <DocsPageModule.DocsPageContentText
                    text={` Please reach out to your third party viewability vendor (including IAS & MOAT) to validate your viewability tags before running viewability campaigns through AppLovin.`}
                />
            </AlertMessage>

            <DocsPageModule.DocsPageHeader
                type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                text={'Setting Up Viewability Tags'}
            />
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={`In order to measure viewability for ALX creatives, you must have a direct relationship with one of the supported viewability vendors. `
                        + `You can find a list of supported viewability vendors under `}
                />
                <DocsPageModule.DocsPageContentAnchor
                    text={`Member Companies`}
                    to={`https://iabtechlab.com/working-groups/open-measurement-working-group/#Member_Companies`}
                    newWindow={true}
                />
                <DocsPageModule.DocsPageContentText
                    text={` on the Open Measurement Working Group page. `
                        + `Work with your third party viewability vendor to set up the viewability tags discussed in this section.`}
                />
            </DocsPageModule.DocsPageContent>
            <DocsPageModule.DocsPageContent
                text={`Viewability tags are specific to each viewability vendor. `
                    + `However, in general you can set up viewability tags in the ways described below, depending on the ad format:`}
            />
            <DocsPageModule.DocsPageUnorderedList>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText
                        text={`For HTML tags, include your viewability vendors’ OMID/javascript tag in the HTML code. `
                            + `Work with your viewability vendor to obtain the tag and for guidance on where to insert it in your HTML.`}
                    />
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={`You can implement VAST XML video measurement tags in-app by adding either an `} />
                    <DocsPageModule.DocsPageContentCode children={`<Extension>`} />
                    <DocsPageModule.DocsPageContentText text={` or `} />
                    <DocsPageModule.DocsPageContentCode children={`<AdVerifications>`} />
                    <DocsPageModule.DocsPageContentText
                        text={` node to your VAST tag. `
                            + `For each third-party viewability vendor, work directly with your viewability vendor account representative on the specific way they recommend that you integrate the VAST `} />
                    <DocsPageModule.DocsPageContentCode children={`<Extension>`} />
                    <DocsPageModule.DocsPageContentText text={` or `} />
                    <DocsPageModule.DocsPageContentCode children={`<AdVerifications>`} />
                    <DocsPageModule.DocsPageContentText
                        text={` node. For most verification vendors, the following instructions indicate how to implement the verification nodes:`}
                    />
                    <DocsPageModule.DocsPageUnorderedList>
                        <DocsPageModule.DocsPageBulletPoint>
                            <DocsPageModule.DocsPageContentText text={`For VAST versions pre-4.1, include an extension node below the VAST XML, with the `} />
                            <DocsPageModule.DocsPageContentCode children={`<AdVerification>`} />
                            <DocsPageModule.DocsPageContentText text={` node nested inside, as shown here:`} />
                            <CodeSnippet
                                snippets={[ {
                                    title: 'xml',
                                    language: 'xml',
                                    code: `<Extension type="AdVerifications">
   <AdVerifications>
      <Verification vendor="vendor name">
         <JavaScriptResource apiFramework="omid" browserOptional="true">
            <![CDATA[]]>
         </JavaScriptResource>
         <TrackingEvents>
            <Tracking event="verificationNotExecuted">
               <![CDATA[]]>
            </Tracking>
         </TrackingEvents>
         <VerificationParameters>
            <![CDATA[“Vendor name”]>
         </VerificationParameters>
      </Verification>
   </AdVerifications>
</Extension>`
                                } ]}/>
                        </DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageBulletPoint>
                            <DocsPageModule.DocsPageContentText text={`For VAST 4.1, include the `} />
                            <DocsPageModule.DocsPageContentCode children={`<AdVerifications>`} />
                            <DocsPageModule.DocsPageContentText text={` node directly below the VAST XML, as shown here:`} />
                            <CodeSnippet
                                snippets={[ {
                                    title: 'xml',
                                    language: 'xml',
                                    code: `<AdVerifications>
  <Verification vendor="vendor name">
    <JavaScriptResource apiFramework="omid" browserOptional="true">
      <![CDATA[]]>
    </JavaScriptResource>
    <TrackingEvents>
      <Tracking event="verificationNotExecuted">
        <![CDATA[]]>
      </Tracking>
    </TrackingEvents>
    <VerificationParameters>
      <![CDATA[“Vendor name”]>
    </VerificationParameters>
  </Verification>
</AdVerifications>undefined</Extension>`
                                } ]}/>
                        </DocsPageModule.DocsPageBulletPoint>
                    </DocsPageModule.DocsPageUnorderedList>
                </DocsPageModule.DocsPageBulletPoint>
            </DocsPageModule.DocsPageUnorderedList>

            <DocsPageModule.DocsPageHeader
                type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                text={'Testing Your Viewability Tags'}
            />
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={`To test your viewability tags, set up your viewability bid responses as per the `}
                />
                <DocsPageModule.DocsPageContentAnchor
                    text={`test app`}
                    to={`/documentation/mediation/auctions/dsps/testing`}
                    newWindow={false}
                />
                <DocsPageModule.DocsPageContentText text={` instructions, and run test impressions in the test app.`} />
            </DocsPageModule.DocsPageContent>
            <DocsPageModule.DocsPageContent
                text={`From there, you can work with your viewability vendor to confirm tracking is correct. `
                    + `They will instruct you to collect charles logs and track certain calls made by their tags, and/or review reporting in their own viewability platform.`}
            />
            <DocsPageModule.DocsPageContent
                text={`Note that OMSDK tags and postback calls must use secure (HTTPS) URLs. `}
            />

            <DocsPageModule.DocsPageHeader
                type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                text={'Bid Responses'}
            />
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={`To help your viewability partner communicate with the AppLovin SDK properly, you can include the OMID-1 API (value = “7”) in the API array of your bid response (`}
                />
                <DocsPageModule.DocsPageContentAnchor
                    text={`bidresponse.bid.apis`}
                    to={`/documentation/mediation/auctions/dsps/ortb-spec/bid-response#bid-object`}
                    newWindow={false}
                />
                <DocsPageModule.DocsPageContentText text={`). Refer to the `} />
                <DocsPageModule.DocsPageContentAnchor
                    text={`AppLovin OpenRTB 2.5 spec`}
                    to={`/documentation/mediation/auctions/dsps/ortb-spec/introduction`}
                    newWindow={false}
                />
                <DocsPageModule.DocsPageContentText text={` for details.`} />
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageHeader
                type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                text={'Reporting'}
            />
            <DocsPageModule.DocsPageContent
                text={`You can receive viewability reporting on your ads if you have a direct relationship with a third-party viewability vendor. `
                    + `In order to collect viewability measurement on your campaigns, include the third-party measurement tags in your creatives and bid responses by following the instructions above and those from your vendor.`}
            />
            <DocsPageModule.DocsPageContent
                text={`All apps on supported SDK versions automatically integrate with and opt into OM SDK. `
                    + `You can receive viewability reporting for your integrated ads on apps that run a supported SDK version.`}
            />

        </>
    )
}

export default DspViewabilityPage;
